import { HttpService, Query, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { FormOfSettlementWithTaxOffice, PersonKind, TaxReturnFrequency, VatReleaseReasonSource, ZusContribution } from '@skylab-shared/dataModel';
import { Transform } from 'class-transformer';

export interface AccountingOnlineReceiver {
    readonly id: string;
    readonly channelId: string;
    readonly password: string;
    name: string;
}

export interface CompanyParameters {
    readonly accountingOnlineReceiver: AccountingOnlineReceiver;
    readonly advanceIncomeTaxPaymentFrequency: TaxReturnFrequency;
    readonly cashBasis: boolean;
    readonly formOfSettlement: FormOfSettlementWithTaxOffice;
    readonly logo: string;
    readonly otherSeller: {
        readonly city: string;
        readonly countryId: number;
        readonly address: string;
        readonly issueDocumentsToOtherSeller: boolean;
        readonly zipCode: string;
        readonly name: string;
        readonly nip: string;
    } | null;
    readonly systemCountryId: number;
    readonly systemCurrency: string;
    readonly taxOfficeCode: string;
    readonly ueVatPayer: boolean;
    readonly vatPayer: boolean;
    readonly vatReleaseReasonCustomName: string;
    readonly vatReleaseReasonId?: number;
    readonly vatReleaseReasonSource?: VatReleaseReasonSource;
    readonly vatReturnFrequency: TaxReturnFrequency;
    readonly zusContribution: ZusContribution;
    readonly personKind: PersonKind;
    readonly ossRegistered: boolean;
}

class CompanyParametersInternalItemDTO {
    public readonly accountingOnlineReceiver: AccountingOnlineReceiver;
    public readonly advanceIncomeTaxPaymentFrequency: TaxReturnFrequency;
    public readonly bdoNumber: string;
    public readonly cashBasis: boolean;
    public readonly eoriNumber: string;
    public readonly formOfSettlement: FormOfSettlementWithTaxOffice;
    public readonly krsNumber: string;
    public readonly logo: string;
    public readonly ossRegistered: boolean;
    public readonly otherSeller: {
        readonly address: string;
        readonly city: string;
        readonly countryId: number;
        readonly issueDocumentsToOtherSeller: boolean;
        readonly name: string;
        readonly nip: string;
        readonly zipCode: string;
    };
    public readonly personKind: PersonKind;
    public readonly printBdoNumber: boolean;
    public readonly printEoriNumber: boolean;
    public readonly printKrsNumber: boolean;
    public readonly systemCountryId: number;
    public readonly systemCurrency: string;
    public readonly taxOfficeCode: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxpayerDateOfBirth: Date;
    public readonly taxpayerFirstName: string;
    public readonly taxpayerSurname: string;
    public readonly ueVatPayer: boolean;
    public readonly vatPayer: boolean;
    public readonly vatReleaseReasonCustomName: string;
    public readonly vatReleaseReasonId: number;
    public readonly vatReleaseReasonSource?: VatReleaseReasonSource;
    public readonly vatReturnFrequency: TaxReturnFrequency;
    public readonly version: string;
    public readonly zusContribution: ZusContribution;
}

export class CompanyParametersQuery extends Query<CompanyParameters> { }

export class CompanyParametersQueryHandlerBase implements QueryHandler<CompanyParametersQuery, CompanyParameters> {
    private static Url = TaggedUrl.join('myCompany/properties', [Tags.Api]);

    public constructor(private http: HttpService) { }

    public async handleAsync(_: CompanyParametersQuery): Promise<CompanyParameters> {
        const { data } = await this.http.get(CompanyParametersQueryHandlerBase.Url, { dto: CompanyParametersInternalItemDTO });
        return {
            accountingOnlineReceiver: data.accountingOnlineReceiver,
            advanceIncomeTaxPaymentFrequency: data.advanceIncomeTaxPaymentFrequency,
            cashBasis: data.cashBasis,
            formOfSettlement: data.formOfSettlement,
            logo: data.logo,
            otherSeller: data.otherSeller ? {
                address: data.otherSeller.address,
                city: data.otherSeller.city,
                countryId: data.otherSeller.countryId,
                issueDocumentsToOtherSeller: data.otherSeller.issueDocumentsToOtherSeller,
                name: data.otherSeller.name,
                nip: data.otherSeller.nip,
                zipCode: data.otherSeller.zipCode
            } : null,
            systemCountryId: data.systemCountryId,
            systemCurrency: data.systemCurrency,
            taxOfficeCode: data.taxOfficeCode,
            ueVatPayer: data.ueVatPayer,
            vatPayer: data.vatPayer,
            vatReleaseReasonCustomName: data.vatReleaseReasonCustomName,
            vatReleaseReasonId: data.vatReleaseReasonId,
            vatReleaseReasonSource: data.vatReleaseReasonSource,
            vatReturnFrequency: data.vatReturnFrequency,
            zusContribution: data.zusContribution,
            personKind: data.personKind,
            ossRegistered: data.ossRegistered
        };
    }
}
